import { useMemo } from "react";

import CustomTableRowService from "services/CustomTableRow";
import { resolveRelationshipTarget } from "routes/custom_tables/relationshipRoutes";
import { choiceValues } from "routes/custom_tables/choiceHelpers";

const choiceData = column =>
  choiceValues(column?.options).map(value => ({ id: value, name: value }));

const OPERATOR_CHOICES = [
  { id: "or", name: "Match any (OR)" },
  { id: "and", name: "Match all (AND)" },
];

const buildFilter = (column, schema) => {
  const base = {
    key: column.field_id,
    title: column.display_name,
  };

  switch (column.type) {
    case "boolean":
      return { ...base, type: "ruvixxToggle" };
    case "single_select":
      return {
        ...base,
        type: "select",
        data: choiceData(column),
        multiple: true,
        clearable: true,
        json: true,
      };
    case "multi_select":
      return {
        ...base,
        type: "select",
        data: choiceData(column),
        multiple: true,
        clearable: true,
        json: true,
      };
    case "date":
      return { ...base, type: "dateRange" };
    case "number":
    case "percentage":
    case "currency":
      return { ...base, type: "input", inputType: "number" };
    // A searchable record picker over the target model's for_filters service:
    // users filter by name and the selection OR-matches the stored record ids.
    case "relationship": {
      const target = resolveRelationshipTarget(
        column.options?.target_type,
        column.options
      );
      if (!target) return { ...base, type: "input", inputType: "number" };
      return {
        ...base,
        type: "ruvixxSelect",
        multiple: true,
        json: true,
        queryFn: target.queryFn,
      };
    }
    // The picker's options are the column's distinct existing element values,
    // fetched as you type; the selection OR-matches by exact element overlap.
    case "list":
      return {
        ...base,
        type: "ruvixxSelect",
        multiple: true,
        json: true,
        queryFn: filters =>
          CustomTableRowService.getListValuesForFilters(
            schema.slug,
            column.field_id,
            filters
          ),
      };
    case "short_text":
    case "email":
    case "url":
    case "phone":
    default:
      return { ...base, type: "input" };
  }
};

// A multi_select carries an AND/OR match mode. The mode lives in its own
// select filter keyed ``op:<field_id>`` (``:`` can't appear in a field_id, so
// it never collides with a real column) and rides the standard select
// machinery — render, query-param encode/decode — with no special-casing.
const operatorFilter = column => ({
  key: `op:${column.field_id}`,
  title: `${column.display_name} match`,
  type: "select",
  data: OPERATOR_CHOICES,
  multiple: false,
  clearable: false,
});

// The app-wide tags filter (the "Contains / Excludes" + tag picker in
// Advanced Search) is built into BaseTableV3 and sources its options from a
// `tag_id` tag entry, exactly like the ORM tables (e.g. RevenueOpportunities).
// Providing it here makes the custom-table grid use that same widget, which
// encodes to the `search:tags:<uuid>` params the rows table reads. A table
// whose admin named a column `tags` owns that data, so we step aside.
const tagFilters = (schema, tagOptions) =>
  tagOptions?.length && !schema.columns.some(c => c.field_id === "tags")
    ? [{ key: "tag_id", title: "Tag", type: "tag", data: tagOptions }]
    : [];

// Stable default so a one-arg call keeps a referentially-equal memo result.
const NO_TAG_OPTIONS = [];

export const useDynamicFilters = (schema, tagOptions = NO_TAG_OPTIONS) => {
  return useMemo(() => {
    if (!schema || !Array.isArray(schema.columns)) return [];
    const columnFilters = schema.columns.flatMap(column => {
      // Long text is content-only — unindexed and not a query axis, so it
      // exposes no filter (full-text search is the future mechanism for it).
      if (column.type === "text") return [];
      const filter = buildFilter(column, schema);
      return column.type === "multi_select"
        ? [filter, operatorFilter(column)]
        : [filter];
    });
    return [...columnFilters, ...tagFilters(schema, tagOptions)];
  }, [schema, tagOptions]);
};

export default useDynamicFilters;
